import { motion } from 'framer-motion';
import { Settings2, Shield, Ruler, Download, Info } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useQRStore, ErrorCorrectionLevel, DownloadFormat } from '@/stores/qrStore';
import { errorCorrectionLevels, resolutionOptions } from '@/lib/design-constants';
import { cn } from '@/lib/utils';

export function AdvancedTab() {
  const { currentQR, setDesignSettings } = useQRStore();
  const { designSettings } = currentQR;

  return (
    <div className="space-y-6">
      {/* Error Correction Level */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-primary" />
          <Label className="text-sm font-medium">Error Correction Level</Label>
          <Tooltip>
            <TooltipTrigger>
              <Info className="h-3.5 w-3.5 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>Higher error correction means the QR code can still be scanned even if partially damaged or obscured. Use H (30%) when adding a logo.</p>
            </TooltipContent>
          </Tooltip>
        </div>

        <RadioGroup
          value={designSettings.errorCorrectionLevel}
          onValueChange={(value) => setDesignSettings({ errorCorrectionLevel: value as ErrorCorrectionLevel })}
          className="grid grid-cols-2 gap-2"
        >
          {errorCorrectionLevels.map((level, index) => {
            const isActive = designSettings.errorCorrectionLevel === level.id;
            
            return (
              <motion.div
                key={level.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <label
                  htmlFor={`ec-${level.id}`}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all duration-200',
                    isActive
                      ? 'border-primary bg-primary/10'
                      : 'border-border/50 hover:border-primary/50 hover:bg-muted/50'
                  )}
                >
                  <RadioGroupItem value={level.id} id={`ec-${level.id}`} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{level.name}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{level.description}</p>
                  </div>
                </label>
              </motion.div>
            );
          })}
        </RadioGroup>
      </div>

      {/* QR Size */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Ruler className="h-4 w-4 text-primary" />
          <Label className="text-sm font-medium">QR Code Size</Label>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Size</span>
            <span className="text-sm font-mono">{designSettings.width}px</span>
          </div>
          <Slider
            value={[designSettings.width]}
            onValueChange={([value]) => setDesignSettings({ width: value, height: value })}
            min={200}
            max={2000}
            step={50}
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>200px</span>
            <span>2000px</span>
          </div>
        </div>
      </div>

      {/* Margin / Quiet Zone */}
      <div className="space-y-2">
        <div className="flex justify-between">
          <div className="flex items-center gap-2">
            <Label>Margin (Quiet Zone)</Label>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>The quiet zone is the blank margin around the QR code. A larger margin makes scanning easier.</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <span className="text-sm font-mono">{designSettings.margin}px</span>
        </div>
        <Slider
          value={[designSettings.margin]}
          onValueChange={([value]) => setDesignSettings({ margin: value })}
          min={0}
          max={50}
          step={5}
        />
      </div>

      {/* Download Settings */}
      <div className="space-y-4 p-4 rounded-lg border border-border/50">
        <div className="flex items-center gap-2">
          <Download className="h-4 w-4 text-primary" />
          <Label className="text-sm font-medium">Download Settings</Label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Format</Label>
            <Select
              value={designSettings.downloadFormat}
              onValueChange={(value) => setDesignSettings({ downloadFormat: value as DownloadFormat })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="png">PNG</SelectItem>
                <SelectItem value="svg">SVG</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Resolution</Label>
            <Select
              value={designSettings.resolution.toString()}
              onValueChange={(value) => setDesignSettings({ resolution: parseInt(value) })}
              disabled={designSettings.downloadFormat === 'svg'}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {resolutionOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {designSettings.downloadFormat === 'svg' && (
          <p className="text-xs text-muted-foreground">
            SVG files are resolution-independent and can be scaled to any size.
          </p>
        )}
      </div>

      {/* Info Panel */}
      <div className="p-4 rounded-lg bg-muted/50 space-y-2">
        <div className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Current Settings</span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Error Correction:</span>
            <span className="font-mono">{designSettings.errorCorrectionLevel}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Size:</span>
            <span className="font-mono">{designSettings.width}×{designSettings.height}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Margin:</span>
            <span className="font-mono">{designSettings.margin}px</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Format:</span>
            <span className="font-mono uppercase">{designSettings.downloadFormat}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
